import React from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LabelList, Cell } from "recharts";
import { formatValue, formatPercentage } from "./Utils";
import _ from "underscore";
import { fillColors } from "../constants.js";


//return the groupings in the order of the measure, including "Other" if the number of groupings is more than the max groupings when specified
function getGroupings(data, maxGroupings, includeOthers = false) {
	if (!data || data.length === 0) {
		return [];
	}

	var groupings = [];
	var groupedData = _.groupBy(data, "grouping");
	
	// Check if all items have the same or missing grouping
	const uniqueGroupings = Object.keys(groupedData);
	if (uniqueGroupings.length === 0) {
		console.warn('BarChartComponent_v2: No grouping values found in data');
		return [];
	}
	
	for (const group in groupedData) {
		// Skip null, undefined, or empty grouping values
		// Note: _.groupBy converts null/undefined to strings "null"/"undefined"
		if (!group || group === 'null' || group === 'undefined' || group === '') {
			continue;
		}
		
		let groupSum = groupedData[group].reduce((acc, curr) => acc + (curr.measure || 0), 0);

		groupings.push({
			grouping: group,
			measure: groupSum,
		});
	}

	// If no valid groupings found, return empty array
	if (groupings.length === 0) {
		return [];
	}

	groupings.sort((a, b) => b.measure - a.measure);

	let returnValue = groupings.map(item => item.grouping).slice(0, maxGroupings);

	// if the number of groupings is more than the max groupings and other should be included, add "Other" value
	if (includeOthers && groupings.length > maxGroupings) {
		returnValue.push("Other");
	}

	return returnValue;
}

function keepOnlyTopGroupings(data, groupings) {
	var result = [];

	if (data && data.length > 0) {
		data.forEach(item => {
			if (groupings.find(group => group === item.grouping)) {
				result.push(item);
			} else {
				result.push({
					...item,
					grouping: "Other"
				})
			}
		});
	}

	return result;
}

function prepareData(data, maxGroupings, dateFormat) {
	if (!data || data.length === 0) {
		return [];
	}

	// check whether the grouping has more than 10 values and order the grouping accordingly
	const groupings = getGroupings(data, maxGroupings);	
	
	// If we only have one grouping, check if we have multiple time periods
	// If so, use time periods to create separate bars as a fallback
	if (groupings.length === 1) {
		const uniqueTimes = [...new Set(data.map(item => item.time))].filter(Boolean);
		
		if (uniqueTimes.length > 1) {
			console.warn('BarChartComponent_v2: Only one grouping found but multiple time periods. Using time periods to create bars.', {
				grouping: groupings[0],
				timePeriods: uniqueTimes.length,
				totalItems: data.length
			});
			
			// Fallback: Group by time instead of grouping
			const d = _.groupBy(data, "time");
			const result = [];
			let idx = 0;
			
			for (const timeKey in d) {
				let total = 0;
				for (let i = 0; i < d[timeKey].length; i++) {
					total += d[timeKey][i].measure || 0;
				}
				
				// Format the time key using dateFormat if it's a date
				const formattedTime = timeKey;
				
				result.push({
					_name: formattedTime,
					_original: formattedTime,
					_total: total,
					value: total,
					_colorIndex: idx % fillColors.length,
				});
				idx++;
			}
			
			// Sort by value descending
			result.sort((a, b) => b.value - a.value);
			return result;
		}
	}
	
	const preparedData = keepOnlyTopGroupings(data, groupings);

	// Group by grouping key to sum totals for each grouping
	var d = _.groupBy(preparedData, "grouping");

	var result = [];

	// Create one row per grouping with a single value field
	// This allows each grouping to display as a full-width bar
	var idx = 0;
	for (var key in d) {
		var total = 0;

		// Sum all measures for this grouping across all time periods
		for (var i = 0; i < d[key].length; i++) {
			total += d[key][i].measure || 0;
		}

		// Create a row with the grouping name, total value, and color index
		result.push({
			_name: key,
			_original: key,
			_total: total,
			value: total, // Single value field for the bar
			_colorIndex: idx % fillColors.length, // Store color index for this grouping
		});
		idx++;
	}

	// Sort by value descending to show largest bars first
	result.sort((a, b) => b.value - a.value);

	return result;
}

export const BarChartComponent = (props) => {
    const { data, title,  dateFormat = "MMM D",    orientation = "vertical" } = props;


	const maxGroupings = 10;
	
	// Debug: Log incoming data to help diagnose issues
	React.useEffect(() => {
		if (data && data.length > 0) {
				}
	}, [data, title]);
	
	const pdata = prepareData(data, maxGroupings, dateFormat);

    // Use a single bar with custom Cell components to color each bar differently
    // Each row (grouping) will display as a full-width bar with its own color
    const bars = (
        <Bar
            barSize={orientation === "vertical" ? 100 : 60}
            type="monotone"
            dataKey="value"
        >
            {pdata.map((entry, index) => (
                <Cell 
                    key={`cell-${index}`} 
                    fill={fillColors[entry._colorIndex || (index % fillColors.length)]} 
                />
            ))}
            {orientation === "horizontal" ? (
                <LabelList 
                    dataKey="_name" 
                    content={renderAxisLabel}
                />
            ) : null}
        </Bar>
    );
	


	
	return (
		<div className="flex flex-col h-full flex-1 overflow-hidden bg-card border border-card-bd rounded-card p-4">
			{pdata && pdata.length > 0 ? (
				<div className="flex-1 min-h-0 relative">
					<div className="absolute top-0 left-0 z-10 p-2 pointer-events-none">
						<div className="text-text text-lg font-semibold widget-title" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
							{title}
						</div>
					</div>
					<div className="absolute inset-0 top-8">
						<ResponsiveContainer width="100%" height="100%">
                    <BarChart
                        layout={orientation === "horizontal" ? "vertical" : "horizontal"}
						data={pdata}
						barCategoryGap="20%"
						barGap={5}
						margin={{
							top: orientation === "vertical" ? 50 : 30,
							left: orientation === "horizontal" ? 0 : 0,
							bottom: 20,
							right: 40,
						}}
					>
						{/* <CartesianGrid strokeDasharray="3 3" /> */}
                        {orientation === "horizontal" ? (
                            <>
                                <XAxis type="number" tick={{ fontSize: 12 }} tickFormatter={formatYAxis} />
                                <YAxis type="category" dataKey="_name" tick={false} />
                            </>
                        ) : (
                            <>
                                <XAxis dataKey="_name" tick={{ fontSize: 12 }} />
                                <YAxis tick={{ fontSize: 12 }} tickFormatter={formatYAxis} />
                            </>
                        )}
						<Tooltip
							content={(props) => renderTooltip({...props, dateFormat, validData: pdata, valueKey: 'value'})}
							cursor={{ fill: "#F4F4F4" }}
							wrapperStyle={{zIndex: 10000 }}
						/>

                        {bars}

						{/* <Legend
							verticalAlign={verticalAlign}
							align={align}
							layout={layout}
							// height={20}
							iconType="circle"
							formatter={renderLegend}
							wrapperStyle={{ paddingLeft: 10 }}
						/> */}
					</BarChart>
						</ResponsiveContainer>
					</div>
				</div>
			) : (
				<div className="flex-1 min-h-0 relative">
					<div className="absolute inset-0 flex items-center justify-center text-text-2">
						No data available
					</div>
				</div>
			)}
		</div>
	);
};

function formatYAxis(value) {
	return formatValue(value, "currency", false);
}


const renderTooltip = ({ active, payload, dateFormat, validData, valueKey }) => {
	if (active && payload && payload.length) {
		const hoveredPayload = payload[0];
		const value = hoveredPayload.value || hoveredPayload.payload._total || 0;
		const name = hoveredPayload.payload._original || hoveredPayload.name || '';
		
		// Calculate total from all data points if validData is provided
		// Otherwise use the current value as fallback
		const total = validData && validData.length > 0
			? validData.reduce((sum, item) => {
				const itemValue = item[valueKey || 'value'] || item._total || 0;
				return sum + (itemValue || 0);
			}, 0)
			: value; // Fallback to value if no data available
		
		const percentageOfTotal = total > 0 ? ((value / total) * 100) : 0;
		const percentageOfTotalText = `${formatPercentage(percentageOfTotal, false)} of total`;

		return (
			<div className="custom-tooltip bg-surface border border-card-bd shadow-float px-4 py-3 p-1 minw-64 ml-8 rounded-card">
				<div className="flex flex-col mb-2">
					<div className="value" key={name}>
						<div className="flex items-center mb-2">
							<div className="flex-1 text-text-3">{name}</div>
						</div>
						<div className="flex items-end">
							<div className="text-xl font-semibold mr-3 flex items-baseline text-text-2">
								{`${formatValue(value, "currency", false)}`}
							</div>
						</div>
					</div>
				</div>
				
				<div className="text-text-2">
					{percentageOfTotalText}
				</div>
			</div>
		);
	}

	return null;
};

const renderAxisLabel = (props) => {
	const { x, y, height, value } = props;
	if (!value || x == null || y == null) return null;

	// Position label right next to the Y-axis (x position should be at the start of the bar)
	const labelY = y + (height || 0) / 2; // Center vertically with the bar
	
	return (
		<text 
			// x={labelX} 
			x={70}
			y={labelY} 
			fill="#000000" 
			fontSize="12" 
			textAnchor="start"
			dominantBaseline="middle"
		>
			{value}
		</text>
	);
};
